/*
 * Copyright (c) 2020.  Rikesh Lal Shrestha
 * All Rights Reserved
 */
import React, { Component, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';

import '../assets/scss/styles.scss';

import {
  VerticalLayout,
  AuthLayout,
  VerticalSystemLayout,
  VerticalSystemSignUpLayout,
} from '../layout';

import { ADMIN_ACCESS_CODE, CLIENT_ACCESS_CODE } from '../constants/appConfig';
import { getLocalStorage } from '../utils/storageUtil';
import Loader from '../components/shared/Loader';

import {
  RestrictAdminRoute,
  RestrictClientRoute,
  RestrictSystemRoute,
} from '../routes/RestrictRoute';
import { PrivateAdminRoute, PrivateClientRoute, PrivateSystemRoute } from '../routes/PrivateRoute';

// Pages
const OauthLogin = React.lazy(() => import('./auth/OauthLogin'));
const SystemLogin = React.lazy(() => import('./auth/SystemLogin'));
const SystemCompanySignUp = React.lazy(() => import('./auth/CompanySignUp'));
const AdminLogin = React.lazy(() => import('./auth/AdminLogin'));
const ClientLogin = React.lazy(() => import('./auth/ClientLogin'));
const Register = React.lazy(() => import('./auth/Register/containers'));
const ForgetPassword = React.lazy(() => import('./auth/ForgetPassword/containers'));
const ResetPassword = React.lazy(() => import('./auth/ResetPassword/containers'));
const ClientAppLayout = React.lazy(() => import('../layout/clientLayout/ClientLayout'));
const ForceChangePassword = React.lazy(() => import('./admin/passwordChange'));
const ForceChangeClientPassword = React.lazy(() => import('./client/passwordChange'));
const Privacy = React.lazy(() => import('./client/privacy'));

const System = React.lazy(() => import('./system'));
const Admin = React.lazy(() => import('./admin'));
const Client = React.lazy(() => import('./client'));

const DirectLink = React.lazy(() => import('./exception/DirectLink'));
const Page403 = React.lazy(() => import('./exception/Error403'));
const Page404 = React.lazy(() => import('./exception/Error404'));
const Page500 = React.lazy(() => import('./exception/Error500'));

import { Notification } from '../components/shared/Toast/Notification';

class App extends Component {
  render() {
    return (
      <Suspense fallback={<Loader message={'Loading. Please wait...'} />}>
        <Notification />
        <Switch>
          <Route exact path="/403" name="Page 403" render={props => <Page403 {...props} />} />
          <Route exact path="/404" name="Page 404" render={props => <Page404 {...props} />} />
          <Route exact path="/500" name="Page 500" render={props => <Page500 {...props} />} />
          <Route exact path={`/oauth/:oauthParam`} component={OauthLogin} />
          <Route exact path={`/admin/enforcepassword`} component={ForceChangePassword} />
          <Route exact path={`/client/enforcepassword`} component={ForceChangeClientPassword} />
          <Route exact path={`/privacy`} component={Privacy} />
          <RestrictClientRoute exact path={'/'} layout={AuthLayout} component={ClientLogin} />
          <RestrictSystemRoute
            exact
            path={'/system/login'}
            layout={AuthLayout}
            component={SystemLogin}
          />
          <RestrictSystemRoute
            path={'/system/sign-up'}
            layout={VerticalSystemSignUpLayout}
            component={SystemCompanySignUp}
          />
          <RestrictAdminRoute
            exact
            path={'/admin/login'}
            layout={AuthLayout}
            component={AdminLogin}
          />
          <RestrictClientRoute exact path={'/register'} layout={AuthLayout} component={Register} />

          <RestrictAdminRoute
            exact
            path={'/forget-password/admin'}
            layout={AuthLayout}
            component={ForgetPassword}
          />
          <RestrictClientRoute
            exact
            path={'/forget-password/client'}
            layout={AuthLayout}
            component={ForgetPassword}
          />

          <PrivateSystemRoute
            path={'/system'}
            layout={VerticalSystemLayout}
            component={System}
            RedirectUrl={`/system/login`}
          />
          <PrivateAdminRoute
            path={'/admin'}
            layout={VerticalLayout}
            component={Admin}
            RedirectUrl={
              getLocalStorage(ADMIN_ACCESS_CODE)
                ? `/${getLocalStorage(ADMIN_ACCESS_CODE)}/admin/login`
                : `/admin/login`
            }
          />

          <PrivateClientRoute
            path={'/client'}
            layout={ClientAppLayout}
            component={Client}
            RedirectUrl={
              getLocalStorage(CLIENT_ACCESS_CODE) ? `/${getLocalStorage(CLIENT_ACCESS_CODE)}` : ''
            }
          />

          <Route
            exact
            path="/:companyCode/link"
            name="Direct link"
            render={props => <DirectLink {...props} />}
          />
          <RestrictAdminRoute
            exact
            path={'/:companyCode/admin/login'}
            layout={AuthLayout}
            component={AdminLogin}
          />
          <RestrictAdminRoute
            exact
            path={'/:companyCode/admin/reset-password'}
            layout={AuthLayout}
            component={ResetPassword}
          />
          <RestrictClientRoute
            exact
            path={'/:companyCode/client/reset-password'}
            layout={AuthLayout}
            component={ResetPassword}
          />
          <RestrictAdminRoute
            exact
            path={'/:companyCode/forget-password/admin'}
            layout={AuthLayout}
            component={ForgetPassword}
          />
          <RestrictClientRoute
            exact
            path={'/:companyCode/forget-password/client'}
            layout={AuthLayout}
            component={ForgetPassword}
          />
          <RestrictClientRoute
            exact
            path={'/:companyCode/register'}
            layout={AuthLayout}
            component={Register}
          />
          <RestrictClientRoute
            exact
            path={'/:companyCode'}
            layout={AuthLayout}
            component={ClientLogin}
          />

          <Route render={props => <Page404 {...props} />} />
        </Switch>
      </Suspense>
    );
  }
}

export default App;
